'use client';

import { ChevronDown, Loader2, Plus } from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';

import { Fragment, useCallback, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { ShortcutTooltip, Tooltip } from '@/shared/components';
import { ShortcutTooltipContentRenderer } from '@/shared/components/shortcut-tooltip/shortcut-tooltip-client';
import { KeyboardKeys, getCommandKeyboardKey } from '@/shared/utils/navigation';

import { getFormInputFieldObject } from '@/utils/common';
import { ADD_FIELD_OPTIONS_NAMES_MAP_WITH_INPUT_AND_PRE_DEFINED_INPUT_TYPE } from '@/utils/constants';

import {
	IAddFieldDropdownOption,
	IAddFieldDropdownProps,
	IAddFieldDropdownSubOption,
} from '../../types/form-editing-types';
import { ADD_FIELD_DROPDOWN_OPTION_TO_ICONS_MAP } from '../constants/add-fied-dropdown-icon-map';
import {
	ADD_FIELD_DROPDOWN_LIST,
	ADD_FIELD_SUB_OPTIONS_MAP_WITH_OPTION_NAME,
} from '../constants/form-edit-constants';

function AddFieldDropdown({
	disableShortcuts = false,
	formId,
	onAddOrEditField,
	addFieldMode,
	selectedInputField,
	fieldOptions = ADD_FIELD_DROPDOWN_LIST,
	disabled = false,
	loading = false,
	staticFieldNameToDisplay = '',
	overrideTooltipContent = '',
	compactTrigger = false,
	primaryCompactTrigger = false,
}: IAddFieldDropdownProps) {
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [open, setOpen] = useState(false);

	const isEditMode = addFieldMode === 'EDIT';

	const handleAddOrEditField = useCallback(
		(fieldOption: IAddFieldDropdownSubOption) => () => {
			if (!fieldOption.inputFieldType) return;

			const isSameFieldTypeSelected =
				isEditMode &&
				selectedInputField?.type === fieldOption.inputFieldType &&
				selectedInputField?.preDefinedInputType === fieldOption.preDefinedInputType;

			if (isSameFieldTypeSelected) return;

			const field = getFormInputFieldObject({
				formId,
				existingId: selectedInputField?.id,
				type: fieldOption.inputFieldType,
				preDefinedInputType: fieldOption.preDefinedInputType ?? undefined,
				question: selectedInputField?.question ?? fieldOption.defaultQuestion ?? '',
				description: selectedInputField?.description ?? fieldOption.defaultDescription ?? '',
				required: selectedInputField?.required ?? true,
				options: fieldOption.inputFieldOptions
					? (selectedInputField?.options ?? fieldOption.inputFieldOptions)
					: undefined,
				fileTypes: fieldOption.inputFieldFileTypes,
				valid: true,
			});

			onAddOrEditField(field);
		},
		[formId, isEditMode, selectedInputField, onAddOrEditField]
	);

	const fieldNameToDisplay = useMemo(() => {
		if (!selectedInputField) return null;

		const parentInputFieldName =
			ADD_FIELD_OPTIONS_NAMES_MAP_WITH_INPUT_AND_PRE_DEFINED_INPUT_TYPE[
				selectedInputField.preDefinedInputType ?? selectedInputField.type
			];
		const subOptions = ADD_FIELD_SUB_OPTIONS_MAP_WITH_OPTION_NAME[parentInputFieldName];

		if (subOptions?.length) {
			const subOption = subOptions.find((option) => {
				if (selectedInputField.preDefinedInputType) {
					return option.preDefinedInputType === selectedInputField.preDefinedInputType;
				}
				return option.inputFieldType === selectedInputField.type;
			});

			return subOption?.label ?? subOption?.name;
		}

		const existingOption = fieldOptions.find((option) => option.name === parentInputFieldName);
		if (existingOption) return existingOption.label ?? existingOption.name;

		return parentInputFieldName;
	}, [selectedInputField]);

	const scrollButtonIntoView = useCallback(() => {
		buttonRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
	}, []);

	useHotkeys(
		`${getCommandKeyboardKey()}+K`,
		() => {
			scrollButtonIntoView();
			setOpen((prev) => !prev);
		},
		{ enableOnFormTags: true, preventDefault: true, enabled: !isEditMode },
		[scrollButtonIntoView]
	);

	const dropdownContent = (
		<DropdownMenu
			onOpenChange={(newOpen) => {
				if (!disabled) setOpen(newOpen);
			}}
			open={open && !disabled && !loading}
		>
			<DropdownMenuTrigger
				asChild
				disabled={disabled}
			>
				{isEditMode ? (
					<div>
						<ShortcutTooltip
							text="Edit question type"
							keys={[getCommandKeyboardKey(), KeyboardKeys.Shift, 'E']}
							hotKeyCallback={() => {
								if (disabled) return;
								scrollButtonIntoView();
								setOpen((prev) => !prev);
							}}
							hotKeyOptions={{
								preventDefault: true,
								enableOnFormTags: true,
								enabled: !disableShortcuts,
							}}
							enableInAllScopes
							overrideTooltipContent={overrideTooltipContent}
						>
							<div>
								<Button
									ref={buttonRef}
									variant={'ghost'}
									className="group flex items-center gap-1 p-0 text-sm font-medium leading-5 text-secondary hover:bg-transparent disabled:cursor-not-allowed disabled:opacity-50"
									disabled={disabled}
								>
									<span>{fieldNameToDisplay ?? staticFieldNameToDisplay ?? 'Change question'}</span>
									<ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
								</Button>
							</div>
						</ShortcutTooltip>
					</div>
				) : compactTrigger ? (
					<Button
						ref={buttonRef}
						variant={primaryCompactTrigger ? 'default' : 'outline'}
						size="sm"
						className="mt-1 gap-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
						disabled={disabled || loading}
					>
						{loading ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
						Add question
					</Button>
				) : (
					<div className="mx-auto w-fit">
						<Button
							ref={buttonRef}
							variant="outline"
							className="m-auto flex w-full max-w-[300px] justify-center gap-2 border border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
							disabled={disabled || loading}
						>
							{loading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Plus className="h-4 w-4" />
							)}
							<span className="inline-block text-sm font-medium leading-6">Add new question</span>
							<ShortcutTooltipContentRenderer
								keys={[getCommandKeyboardKey(), 'K']}
								keysClassName="text-gray-400"
							/>
						</Button>
					</div>
				)}
			</DropdownMenuTrigger>
			<DropdownMenuContent
				className="w-56"
				align="center"
				onCloseAutoFocus={(e) => e.preventDefault()}
			>
				{fieldOptions.map((option: IAddFieldDropdownOption) => {
					const { subOptions, withBottomBorder } = option;
					const Icon = ADD_FIELD_DROPDOWN_OPTION_TO_ICONS_MAP[option.name];

					if (subOptions) {
						return (
							<Fragment key={option.name}>
								<DropdownMenuSub>
									<DropdownMenuSubTrigger className="gap-2">
										{Icon && Icon}
										<span>{option.label ?? option.name}</span>
									</DropdownMenuSubTrigger>
									<DropdownMenuPortal>
										<DropdownMenuSubContent className="ml-2">
											{subOptions.map((subOption: IAddFieldDropdownSubOption) => {
												const subOptionIcon =
													ADD_FIELD_DROPDOWN_OPTION_TO_ICONS_MAP[subOption.name];
												return (
													<DropdownMenuItem
														key={subOption.name}
														onClick={handleAddOrEditField(subOption)}
													>
														{subOptionIcon && subOptionIcon}
														<span className="text-sm font-medium leading-5 text-slate-700">
															{subOption.label ?? subOption.name}
														</span>
													</DropdownMenuItem>
												);
											})}
										</DropdownMenuSubContent>
									</DropdownMenuPortal>
								</DropdownMenuSub>
								{withBottomBorder && <DropdownMenuSeparator />}
							</Fragment>
						);
					}

					return (
						<Fragment key={option.name}>
							<DropdownMenuItem
								className="flex items-center gap-2"
								onClick={handleAddOrEditField(option)}
							>
								{Icon && Icon}
								<span>{option.label ?? option.name}</span>
							</DropdownMenuItem>
							{withBottomBorder && <DropdownMenuSeparator />}
						</Fragment>
					);
				})}
			</DropdownMenuContent>
		</DropdownMenu>
	);

	if ((disabled || loading) && !isEditMode) {
		return (
			<Tooltip content={overrideTooltipContent || 'Creating form...'}>
				<div>{dropdownContent}</div>
			</Tooltip>
		);
	}

	return dropdownContent;
}

export default AddFieldDropdown;
